import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Row,
  Button,
  Input,
  Label,
} from "reactstrap";
import { dashboard, fetchFormData, tickets, ticketsManagement } from "../../helpers/backend_helper";
import { toast } from "react-toastify";
import { CommonDatePicker } from "../../components/Common/Datepicker/CommonDatePicker";
import { useDispatch, useSelector } from "react-redux";
import { getAllUSer } from "../../store/actions";
import { useNavigate } from "react-router-dom";
import { formatDateOnly, getDefaultDates, getEndOfDay, getEndOfDayTimestamp, getStartOfDay, getStartOfDayTimestamp, getTimestamp } from "../../helpers/formatDate";
import { getBadgeColor } from "../../helpers/general";
import Breadcrumbs from "../../components/Common/Breadcrumbs";
import Datatable from "../../components/Common/Datatable/Datatable";
import { CommonMyDatePicker } from "../../components/Common/Datepicker/CommonMyDatePicker";

export default function TicketHistory() {
  const defaultDates = getDefaultDates();
  const dispatch = useDispatch();
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [uniqueIncidentTypes, setUniqueIncidentTypes] = useState([]);
  const [uniqueStatuses, setUniqueStatuses] = useState([]);
  const [startDate, setStartDate] = useState(defaultDates.start.getTime());
  const [endDate, setEndDate] = useState(defaultDates.end.getTime());
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketHistory, setTicketHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchStatus, setSearchStatus] = useState("");
  const [searchSerialId, setSearchSerialId] = useState("");

  const navigate = useNavigate();

  const { allUsersnvehicle } = useSelector(
    (state) => state.Common || {}
  );

  const fetchReports = async (from, to) => {
    setLoading(true);
    const payload = {
      act: "dashboard",
      subact: "reports",
      info: {},
      filter: {
        fromDate: getStartOfDayTimestamp(from),
        toDate: getEndOfDayTimestamp(to),
      },
    };

    try {
      const response = await dashboard(payload);
      if (response?.message) {
        const data = response?.data || [];
        data.sort((a, b) => {
          const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return timeB-timeA ; // Descending order
        });
      
        setReports(data);
        setFilteredReports(data);

        // Extract unique statuses
        const statuses = [...new Set(data.map((item) => item.status))].filter(
          Boolean
        );
        setUniqueStatuses(statuses);
        return data;
      }
      return [];
    } catch (error) {
      toast.error("Error in fetching reports", {
        position: "top-right",
        autoClose: 3000,
      });
      return [];
    } finally {
      setLoading(false);
    }
  };

  const getAllIncidentType = async () => {
    const payload = {
      act: "common",
      subact: "getAllIncidentType",
      info: {},
    };

    try {
      const response = await fetchFormData(payload);
      if (response?.message) {
        const data = response?.data || [];
        setUniqueIncidentTypes(data);
      }
    } catch (error) {
      toast.error("Error in fetching incident types", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  }

  // Handle date change
  const handleStartDateChange = (timestamp) => {
    setStartDate(getTimestamp(getStartOfDay(timestamp)));
  };

  const handleEndDateChange = (timestamp) => {
    setEndDate(getTimestamp(getEndOfDay(timestamp)));
  };

  // Fetch ticket history by ticket ID
  const fetchTicketHistory = async (ticketId) => {
    setLoading(true);
    setSelectedTicket(ticketId);

    const payload = {
      act: "ticket",
      subact: "history",
      info: {
        ticketId: ticketId,
      },
    };

    try {
      const response = await tickets(payload);
      if (response?.data) {
        setTicketHistory(response.data);
        toast.success("Ticket history fetched successfully", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.error("Error fetching ticket history", {
        position: "top-right",
        autoClose: 3000,
      });
      setTicketHistory([]);
    } finally {
      setLoading(false);
    }
  };

  // Apply date and other filters
  const handleFilterApply = async () => {
    setLoading(true);
    
    try {
      // Fetch reports with current date filters and wait for the result
      const fetchedReports = await fetchReports(startDate, endDate);
      
      // Apply additional filters using the freshly fetched data
      let filtered = [...fetchedReports];
  
      // Filter by status if selected
      if (searchStatus) {
        filtered = filtered.filter(
          (report) => report.status?.toLowerCase() === searchStatus.toLowerCase()
        );
      }
  
      // Filter by serial ID if provided
      if (searchSerialId) {
        filtered = filtered.filter((report) =>
          report.serialId?.toLowerCase()?.includes(searchSerialId?.toLowerCase())
        );
      }
  
      setFilteredReports(filtered);
    } catch (error) {
      console.error("Error applying filters:", error);
      toast.error("Error applying filters", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Reset all filters
  const handleResetFilters = () => {
    // Reset the date filters to default
    const defaultDates = getDefaultDates();
    setStartDate(defaultDates.start.getTime());
    setEndDate(defaultDates.end.getTime());
    
    // Reset other filters
    setSearchStatus("");
    setSearchSerialId("");
    
    // Fetch reports with default dates
    fetchReports(defaultDates.start.getTime(), defaultDates.end.getTime());
  };

  // Initial load - fetch users and initial reports
  useEffect(() => {
    dispatch(getAllUSer());
    getAllIncidentType();
    fetchReports(startDate, endDate); // Initial fetch with default dates
  }, []);

  // Component to render a single action in timeline format
  const TicketActionItem = ({ actionData, index, isLast }) => {
    // Determine the timestamp to use (updatedAt or createdAt)
    const displayTimestamp = actionData.updatedAt || actionData.createdAt;
  
    // Get user who updated/created the ticket
    const actionUser = actionData.updatedBy
      ? actionData.updatedBy
      : actionData.createdBy
      ? actionData.createdBy
      : "Unknown";

  
    return (
      <div className="timeline-item">
        <div className="timeline-line-wrap">
          <div className="timeline-line"></div>
          <div
            className={`timeline-dot ${getBadgeColor(actionData.status)}`}
          ></div>
          {!isLast && <div className="timeline-line"></div>}
        </div>
        <div className="timeline-content">
          <div className="timeline-card">
            <div className="p-3">
              <div className="d-flex justify-content-between mb-2">
                <h5 className="mb-0 font-size-15">
                  <span className={`status-badge ${getBadgeColor(actionData.status)}`}>
                    {actionData.status || ""}
                  </span>
                </h5>
              </div>
              <div className="timeline-details">
                {actionData.actions && (
                  <div className="timeline-detail-item">
                    <i className="mdi mdi-list-status me-1"></i>
                    <span className="text-capitalize">{actionData.actions}</span>
                  </div>
                )}
                <div className="timeline-detail-item">
                  <i className="mdi mdi-calendar-clock me-1"></i>
                  {formatDateOnly(displayTimestamp)}
                </div>
                <div className="timeline-detail-item">
                  <i className="mdi mdi-account me-1"></i>
                  {(() => {
                    const user = allUsersnvehicle?.find(
                      (user) => user.userId === actionUser
                    );
                    return user
                      ? <span className="text-capitalize">{`${user.firstName} ${user.lastName}`}</span>
                      : actionUser;
                  })()}
                </div>
              </div>

              {actionData.comments && (
                <div className="timeline-comments">
                  <p className="mb-0">{actionData.comments}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // When no history is selected yet, show data table
  const renderReportsTable = () => {
   
    
    const columns = [
      {
        name: "Ticket ID",
        selector: (row) => row.serialId,
        sortable: true,
        width: "10%"    
      },
      {
        name: "Status",
        sortable: true,
        selector: (row) => (
          <span className={`badge ${getBadgeColor(row.status)}`}>
            {row.status}
          </span>
        ),
        width: "8%"    
      },
      {
        name: "Incident Type",
        selector: (row) => (uniqueIncidentTypes?.filter((incident => incident.incidentTypeId == row.incidentTypeId))[0]?.incidentName),
        sortable: true,
        width: "12%"    
      },
      {
        name: "Assigned To",
        selector: (row) => (<span className="text-capitalize">{row?.assignedUsers?.trim()}</span>),
        sortable: true,
        width: "15%"    
      },
      {
        name: "Closed By",
        selector: (row) => (row.resolvedByUser.length > 0 ? <span className="text-capitalize">{row.resolvedByUser}</span> : "-"),
        width: "15%"    
      },
      {
        name: "Created On",
        selector: row => row.createdAt ? new Date(row.createdAt).getTime() : 0,
        sortable: true,
        width: "14%",
        cell: row => (
          row.createdAt ? (
            <div>{formatDateOnly(row.createdAt)}</div>
          ) : "-"
        )
      },
      
      {
        name: "Closed On",
        selector: row => {
          const timestamp = row.updatedAt;
          return row.status === "Closed" && timestamp ? new Date(timestamp).getTime() : 0;
        },
        width: "14%",
        sortable: true,
        cell: row => {
          const timestamp = row.updatedAt;
          return row.status === "Closed" && timestamp ? (
            <div>{formatDateOnly(timestamp)}</div>
          ) : "-";
        }
      },
      
      {
        name: "Actions",
        width: "9%",
        cell: (row) => (
          <Button
            color="primary"
            className="btn-rounded btn-sm"
            onClick={() => fetchTicketHistory(row.ticketId)}
          >
            <i className="mdi mdi-history me-1"></i>
            History
          </Button>
        ), 
      },
    ];

    return (
      <div className="mb-4">
         <Card className="filter-card">
          <CardBody>
            <div className="filter-content mt-4">
              <Row className="align-items-end">
                <Col md={2} className="mb-3">
                  <FormGroup>
                    <CommonDatePicker
                      label="Start Date"
                      value={startDate}
                      maxDate={endDate}
                      onChange={handleStartDateChange}
                      name={"Start Date"}
                      preserveTime={true}
                    />
                  </FormGroup>
                </Col>
                <Col md={2} className="mb-3">
                  <FormGroup>
                    <CommonMyDatePicker
                      label="End Date"
                      value={endDate}
                      minDate={startDate}
                      onChange={handleEndDateChange}
                      name={"End Date"}
                      preserveTime={true}
                    />
                  </FormGroup>
                </Col>
                <Col md={3} className="mb-3">
                  <FormGroup>
                    <Label className="form-label">Status</Label>
                    <Input
                      type="select"
                      value={searchStatus}
                      onChange={(e) => setSearchStatus(e.target.value)}
                      className="form-select"
                    >
                      <option value="">All Statuses</option>
                      {uniqueStatuses.map((status, index) => (
                        <option key={index + status} value={status}>
                          {status.charAt(0).toUpperCase() +
                            status.slice(1).toLowerCase()}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={3} className="mb-3">
                  <FormGroup>
                    <Label className="form-label">Ticket ID</Label>
                    <Input
                      type="text"
                      value={searchSerialId}
                      onChange={(e) => setSearchSerialId(e.target.value)}
                      placeholder="Enter Ticket ID"
                      className="search-input"
                    />
                  </FormGroup>
                </Col>
                <Col md={2} className="mb-4">
<div className="filter-header">
              <div className="filter-actions d-flex gap-2">
                <Button
                  color="primary"
                  className="btn-apply"
                  onClick={handleFilterApply}
                  disabled={loading}
                >
                                    Apply
                   
                </Button>
                <Button
                  color="secondary"
                  className="btn-reset"
                  onClick={handleResetFilters}
                  disabled={loading}
                >
                  Reset
                </Button>
              </div>
            </div>
                </Col>
              </Row>
            </div>
            
          </CardBody>
        </Card>
        <Datatable 
          columns={columns}
          data={filteredReports}
        />
      </div>
    );
  };

  // When a ticket is selected, show its history
  const renderTicketHistory = () => {
    const ticketData = selectedTicket
      ? filteredReports.find((r) => r.ticketId === selectedTicket)
      : null;

    // Sort ticket history by updatedAt (or createdAt if updatedAt doesn't exist)
    const historyToDisplay = ticketHistory.length
      ? [...ticketHistory].sort((a, b) => {
          const timeA = a.updatedAt || a.createdAt || 0;
          const timeB = b.updatedAt || b.createdAt || 0;
          return new Date(timeB) - new Date(timeA); // Newest first
        })
      : [];
      
    const handleEditTicket = async (ticketId) => {
      const payload = {
        act: "ticket",
        subact: "get",
        info: { ticketId },
      };
      const response = await ticketsManagement(payload);
      if (response?.message) {
        navigate(`/update-ticket?id=${ticketId}`, {
          state: { ticketData: response.data, editable: true },
        });
      }
    };

   

    return (
      <Card>
        <CardBody>
     
      <div className="ticket-history-container">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <Button
            color="secondary"
            className="btn-back"
            onClick={() => setSelectedTicket(null)}
          >
            <i className="mdi mdi-arrow-left me-1"></i>
            Back to Reports
          </Button>
          <h2 className="history-title mb-0">Ticket History</h2>
          <Button
            color="primary"
            onClick={() => handleEditTicket(ticketData?.ticketId)}
            className="btn-rounded"
          >
            <i className="mdi mdi-pencil me-1"></i>
            Go to Ticket
          </Button>
        </div>

        {ticketData && (
          <div className="ticket-details">
            <Row>
              <Col md={6}>
                <div className="ticket-info-item">
                  <span className="ticket-info-label">Ticket ID</span>
                  <span className="ticket-info-value">{ticketData?.serialId}</span>
                </div>
                <div className="ticket-info-item">
                  <span className="ticket-info-label">Assigned To</span>
                  <span className="ticket-info-value text-capitalize">{ticketData?.assignedUsers}</span>
                </div>
              </Col>
              <Col md={6}>
                <div className="ticket-info-item">
                  <span className="ticket-info-label">Caller</span>
                  <span className="ticket-info-value text-capitalize">{ticketData?.callerName || "-"}</span>
                </div>
                <div className="ticket-info-item">
                  <span className="ticket-info-label">Current Status</span>
                  <span className={`status-badge badge ${getBadgeColor(ticketData?.status)}`}>
                    {ticketData.status}
                  </span>
                </div>
              </Col>
            </Row>
            {ticketData.description && (
              <Row className="mt-3 border-top pt-3">
                <Col>
                  <div className="ticket-description">
                    <span className="ticket-info-label">Description</span>
                    <p className="ticket-description-text mb-0">{ticketData.description}</p>
                  </div>
                </Col>
              </Row>
            )}
          </div>
        )}

        <div className="timeline-container">
          {historyToDisplay.length > 0 ? (
            historyToDisplay.map((item, index) => (
              <TicketActionItem
                key={index}
                index={index}
                actionData={item}
                isLast={index === historyToDisplay.length - 1}
              />
            ))
          ) : (
            <div className="empty-state">
              <i className="mdi mdi-information-outline"></i>
              <h5>No History Records</h5>
              <p>No history records found for this ticket.</p>
            </div>
          )}
        </div>
      </div>
      </CardBody>
      </Card>
    );
  };

  return (
    <div className="page-content">
      <Container fluid>
      <Breadcrumbs title="Control Room" breadcrumbItem="Ticket Log" />
       
            {selectedTicket ? renderTicketHistory() : renderReportsTable()}
         
      </Container>
    </div>
  );
}